import React from 'react';
import { ROLE_MODULE_ORDERS } from '../roleViews';
import { createEmptyState } from './mockData';
import { workbenchReducer } from './reducer';
import type {
  RoleView,
  WorkbenchAction,
  WorkbenchState,
  WorkItem,
} from './types';

interface WorkbenchContextValue {
  state: WorkbenchState;
  dispatch: React.Dispatch<WorkbenchAction>;
  reset: () => void;
}

const WorkbenchContext = React.createContext<WorkbenchContextValue | null>(
  null,
);

export const WorkbenchProvider: React.FC<
  React.PropsWithChildren<{ initialRole?: RoleView }>
> = ({ children, initialRole = 'investmentManager' }) => {
  const [state, dispatch] = React.useReducer(
    workbenchReducer,
    undefined,
    () => ({
      ...createEmptyState(),
      preference: {
        ...createEmptyState().preference,
        roleView: initialRole,
        moduleOrder: ROLE_MODULE_ORDERS[initialRole],
      },
    }),
  );
  // 业务页新建任务后，把任务中心返回的任务同步进工作台待办队列，
  // 保证任务在对应模块详情和跨模块工作台都能看到。
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleTaskChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ workItem?: WorkItem }>).detail;
      if (!detail?.workItem) return;
      dispatch({ type: 'workItem/create', payload: detail.workItem });
    };
    window.addEventListener('platform-task-changed', handleTaskChanged);
    return () => {
      window.removeEventListener('platform-task-changed', handleTaskChanged);
    };
  }, []);

  const reset = React.useCallback(() => {
    dispatch({ type: 'state/reset', payload: createEmptyState() });
  }, []);
  const value = React.useMemo(
    () => ({ state, dispatch, reset }),
    [state, reset],
  );
  return (
    <WorkbenchContext.Provider value={value}>
      {children}
    </WorkbenchContext.Provider>
  );
};

export const useWorkbench = () => {
  const value = React.useContext(WorkbenchContext);
  if (!value)
    throw new Error('useWorkbench must be used inside WorkbenchProvider');
  return value;
};

export const useOptionalWorkbench = () => {
  return React.useContext(WorkbenchContext);
};
